<template>
    <vx-modal v-model:visible="visible" size="lg" :padding="false">
        <page-select
            v-model="selectedTargets"
            :options="targetMappings"
            :getOptionKey="getTargetKey"
            :getOptionLabel="getOptionLabel"
            :customSearch="customSearch"
            data-test="page-select-target"
        >
            <template v-slot:header> Add Targets </template>
            <template v-slot:subheader>
                Add groups or organizational units to <strong>{{ props.messageStandardName }}</strong>
            </template>
            <template v-slot:select-all-right>
                <div class="ml-auto">
                    <vx-dropdown v-if="groupCollections.length" placement="right">
                        <vx-button color="muted" :loading="loadingCollectionsMemberships > 0" size="xs">
                            Select by Collection <font-awesome-icon widthAuto :icon="faCaretDown"></font-awesome-icon>
                        </vx-button>
                        <template v-slot:items>
                            <vx-dropdown-item
                                v-for="groupCollection in groupCollections"
                                :key="groupCollection.id"
                                @click="selectGroupCollection(groupCollection)"
                                :data-test="`group-collection-${groupCollection.id}`"
                            >
                                {{ groupCollection.name }}
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>
                </div>
            </template>
            <template v-slot:option="{ item }">
                <div class="flex justify-between items-center w-full gap-2">
                    <div class="flex items-center gap-2">
                        <span>
                            <strong>{{ item.target_name || '-' }}</strong>
                            <template v-if="item.target_type === 'group'"> - {{ item.target_id }}</template>
                        </span>

                        <div class="shrink-0">
                            <vx-popover
                                v-if="willBeReassigned(item)"
                                hover
                                placement="top"
                                teleport
                                arrow
                                @open="onBadgeHover(item)"
                            >
                                <vx-badge color="info" size="xs">
                                    Reassigned
                                    <font-awesome-icon widthAuto :icon="faCircleInfo" />
                                </vx-badge>
                                <template v-slot:content>
                                    <div class="w-72 max-w-full text-center">
                                        <vx-icon-loading v-if="!getMessageStandardName(item)" spin />
                                        <span v-else>
                                            Will be removed from
                                            <strong>{{ getMessageStandardName(item) }}</strong> and reassigned here.
                                        </span>
                                    </div>
                                </template>
                            </vx-popover>
                            <vx-popover
                                v-else-if="isAssigned(item)"
                                hover
                                placement="top"
                                teleport
                                arrow
                                @open="onBadgeHover(item)"
                            >
                                <vx-badge color="primary" size="xs">
                                    Assigned
                                    <font-awesome-icon widthAuto :icon="faCircleInfo" />
                                </vx-badge>
                                <template v-slot:content>
                                    <div class="w-72 max-w-full text-center">
                                        <span v-if="getTargetAssignment(item) === props.messageStandardId">
                                            Already assigned to this brand standard.
                                        </span>
                                        <vx-icon-loading v-else-if="!getMessageStandardName(item)" spin />
                                        <span v-else>
                                            Already assigned to <strong>{{ getMessageStandardName(item) }}</strong
                                            >. If selected, will be removed and assigned here.
                                        </span>
                                    </div>
                                </template>
                            </vx-popover>
                        </div>
                    </div>

                    <div class="shrink-0">
                        <vx-badge v-if="item.target_type === 'org_unit'" color="violet" size="xs">
                            Org
                            <font-awesome-icon
                                widthAuto
                                :icon="faBuildingFlag"
                                class="text-violet-500"
                            ></font-awesome-icon>
                        </vx-badge>
                        <vx-badge v-if="item.target_type === 'group'" color="slate" size="xs">
                            Group
                            <font-awesome-icon widthAuto :icon="faGrid2Plus" class="text-slate-500"></font-awesome-icon>
                        </vx-badge>
                    </div>
                </div>
            </template>
        </page-select>

        <template v-slot:footer>
            <div class="flex flex-col gap-4">
                <vx-alert v-if="reassignedTargets.length > 0" color="info" :closeable="false" class="mb-0">
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faDiamondExclamation" />
                    </template>
                    <div class="flex justify-between items-center">
                        <span>
                            <strong>{{ pluralized('target', reassignedTargets.length, true) }}</strong> will be
                            reassigned</span
                        >
                        <vx-button @click="deselectReassignedTargets" size="sm" color="info" type="button">
                            Deselect Targets
                            <font-awesome-icon widthAuto :icon="faSquareCheck" />
                        </vx-button>
                    </div>
                </vx-alert>

                <div class="flex flex-col-reverse gap-4 md:flex-row">
                    <vx-button
                        @click="visible = false"
                        type="button"
                        data-test="cancel-button"
                        class="grow md:w-20 md:grow-0"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-popover class="grow w-full" arrow placement="top" hover>
                        <vx-button
                            @click="submitAddTargets"
                            data-test="submit-button"
                            :loading="submitting"
                            :disabled="selectedTargets.length === 0"
                            type="button"
                            block
                            size="lg"
                            color="primary"
                        >
                            Add Targets <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                        <template v-if="selectedTargets.length === 0" v-slot:content>
                            You must select at least one target
                        </template>
                    </vx-popover>
                </div>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import {
    faBuildingFlag,
    faCaretDown,
    faCircleInfo,
    faCirclePlus,
    faDiamondExclamation,
    faGrid2Plus,
    faSquareCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxModal,
    VxPopover,
} from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import PageSelect from '~/components/general/PageSelect.vue';
import { useToasted } from '~/composables/useToasted';
import { groupCollectionsClient, messageStandardsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import type { TargetMappingType } from '~/services/messageStandards/messageStandardsClient.types';
import type { GroupCollection } from '~/services/groupCollections/groupCollectionsClient.types';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    messageStandardId: number;
    messageStandardName?: string;
    targetMappings: TargetMappingType[];
}>();

const emit = defineEmits<{
    added: [];
}>();

const visible = defineModel<boolean>('visible', { required: true });

const toasted = useToasted();
const selectedTargets = ref<TargetMappingType[]>([]);
const submitting = ref(false);
const messageStandardNames = ref(new Map<number, string>());
const groupCollections = ref<GroupCollection[]>([]);
const loadingCollectionsMemberships = ref(0);
const collectionsMemberships: Record<number, string[]> = {};

const getTargetKey = (target: TargetMappingType) => `${target.target_type}_${target.target_id}`;
const getOptionLabel = (target: TargetMappingType) => target.target_name;

const customSearch = (target: TargetMappingType, searchTerm: string): boolean => {
    if (target.target_name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
    }
    return String(target.target_id).includes(searchTerm);
};

const assignmentMap = computed(() => {
    const map = new Map<string, number>();
    for (const mapping of props.targetMappings) {
        if (mapping.custom_message_standard_id) {
            map.set(getTargetKey(mapping), mapping.custom_message_standard_id);
        }
    }
    return map;
});

const selectedTargetKeys = computed(() => new Set(selectedTargets.value.map(getTargetKey)));

const existingTargetsForMessageStandard = computed(() => {
    return props.targetMappings.filter((mapping) => mapping.custom_message_standard_id === props.messageStandardId);
});

const getTargetAssignment = (target: TargetMappingType) => {
    return assignmentMap.value.get(getTargetKey(target));
};

const isAssigned = (target: TargetMappingType) => {
    return Boolean(getTargetAssignment(target));
};

const isTargetSelected = (target: TargetMappingType) => {
    return selectedTargetKeys.value.has(getTargetKey(target));
};

const willBeReassigned = (target: TargetMappingType) => {
    const assignedTo = getTargetAssignment(target);
    return isTargetSelected(target) && assignedTo && assignedTo !== props.messageStandardId;
};

const reassignedTargets = computed(() => {
    const map = assignmentMap.value;
    return selectedTargets.value.filter((target) => {
        const assignedTo = map.get(getTargetKey(target));
        return assignedTo && assignedTo !== props.messageStandardId;
    });
});

const deselectReassignedTargets = () => {
    const map = assignmentMap.value;
    selectedTargets.value = selectedTargets.value.filter((target) => {
        const assignedTo = map.get(getTargetKey(target));
        return !(assignedTo && assignedTo !== props.messageStandardId);
    });
};

const fetchMessageStandardName = async (messageStandardId: number) => {
    if (messageStandardNames.value.has(messageStandardId)) {
        return;
    }

    try {
        const response = await messageStandardsClient.getMessageStandard({ teamId }, messageStandardId);
        messageStandardNames.value.set(messageStandardId, response.data.name || 'Unknown');
    } catch {
        messageStandardNames.value.set(messageStandardId, 'Unknown');
    }
};

const getMessageStandardName = (target: TargetMappingType) => {
    const messageStandardId = getTargetAssignment(target);
    if (!messageStandardId) {
        return undefined;
    }
    return messageStandardNames.value.get(messageStandardId);
};

const onBadgeHover = (target: TargetMappingType) => {
    const messageStandardId = getTargetAssignment(target);
    if (messageStandardId && messageStandardId !== props.messageStandardId) {
        fetchMessageStandardName(messageStandardId);
    }
};

const loadGroupCollections = async () => {
    try {
        const response = await groupCollectionsClient.getGroupCollections({ teamId });
        groupCollections.value = response.data.data;
    } catch {
        groupCollections.value = [];
    }
};

const selectCollectionMembership = (groupCollectionId: number) => {
    const groupIds = collectionsMemberships[groupCollectionId];

    if (!groupIds) {
        return;
    }

    const keys = selectedTargetKeys.value;
    const targetsToAdd: TargetMappingType[] = groupIds
        .map((groupId) => ({
            target_type: 'group' as const,
            target_id: groupId,
            target_name: groupId,
        }))
        .filter((target) => !keys.has(getTargetKey(target)));

    selectedTargets.value = [...selectedTargets.value, ...targetsToAdd];
};

const selectGroupCollection = async (groupCollection: GroupCollection) => {
    if (collectionsMemberships[groupCollection.id]) {
        selectCollectionMembership(groupCollection.id);
        return;
    }

    loadingCollectionsMemberships.value++;
    collectionsMemberships[groupCollection.id] = [];

    try {
        const response = await groupCollectionsClient.getGroupCollectionGroupMemberships(
            { teamId },
            groupCollection.id
        );

        loadingCollectionsMemberships.value--;
        collectionsMemberships[groupCollection.id] = response.data.memberships ?? [];
        selectCollectionMembership(groupCollection.id);
    } catch (e) {
        loadingCollectionsMemberships.value--;
        catchRequestError(e);
        toasted.error(`It was not possible to select the groups from the collection "${groupCollection.name}"`);
    }
};

const submitAddTargets = async () => {
    if (submitting.value) {
        return;
    }

    if (selectedTargets.value.length === 0) {
        toasted.error('Select at least one target to add.');
        return;
    }

    try {
        submitting.value = true;

        const existingGroupIds = existingTargetsForMessageStandard.value
            .filter((t) => t.target_type === 'group')
            .map((t) => String(t.target_id));

        const existingOrgUnitIds = existingTargetsForMessageStandard.value
            .filter((t) => t.target_type === 'org_unit')
            .map((t) => Number(t.target_id));

        const selectedGroupIds = selectedTargets.value
            .filter((t) => t.target_type === 'group')
            .map((t) => String(t.target_id));

        const selectedOrgUnitIds = selectedTargets.value
            .filter((t) => t.target_type === 'org_unit')
            .map((t) => Number(t.target_id));

        const allGroupIds = [...new Set([...existingGroupIds, ...selectedGroupIds])];
        const allOrgUnitIds = [...new Set([...existingOrgUnitIds, ...selectedOrgUnitIds])];

        await messageStandardsClient.updateTargets({ teamId }, Number(props.messageStandardId), {
            group_ids: allGroupIds,
            org_unit_ids: allOrgUnitIds,
        });

        const newTargets = selectedTargets.value.filter((target) => !isAssigned(target));
        const reassignedCount = reassignedTargets.value.length;

        if (newTargets.length || reassignedCount) {
            let title = '';
            let description: string | undefined;
            let type: 'success' | 'warning' = 'success';

            if (newTargets.length) {
                title = `${pluralized('target', newTargets.length, true)} added`;
                if (reassignedCount) {
                    description = `${pluralized(
                        'target',
                        reassignedCount,
                        true
                    )} removed from their previous brand standards and assigned here`;
                }
            } else {
                type = 'warning';
                title = `${pluralized('target', reassignedCount, true)} reassigned`;
            }

            toasted.show({ title, description, type });
        }

        emit('added');
        visible.value = false;
        selectedTargets.value = [];
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};

watch(visible, (isOpen) => {
    if (isOpen) {
        selectedTargets.value = [];
        loadGroupCollections();
    }
});
</script>
